<template>
  <CenteredModal v-model="localShow" :show-close-button="true" max-width="420px">
    <div class="delete-warning">
      <!-- Warning icon -->
      <div class="delete-warning__icon">
        <v-icon size="48" color="warning">mdi-alert-circle-outline</v-icon>
      </div>

      <!-- Title -->
      <h3 class="delete-warning__title">{{ t('eventDeleteDialog.title') }}</h3>

      <!-- Event name -->
      <p class="delete-warning__event-name">{{ event?.title }}</p>

      <!-- Attendant count info -->
      <div v-if="totalAttendants > 0" class="delete-warning__info">
        <div class="delete-warning__count">
          <v-icon size="20" class="mr-2">mdi-account-group</v-icon>
          <span>{{ t('eventDeleteDialog.registeredCount') }}: <strong>{{ totalAttendants }}</strong></span>
        </div>

        <!-- Breakdown by type -->
        <div class="delete-warning__breakdown">
          <template v-if="hasHomeAway">
            <div v-if="homePlayersCount > 0" class="delete-warning__breakdown-item">
              <span>{{ t('eventDeleteDialog.homePlayers') }}: {{ homePlayersCount }}</span>
            </div>
            <div v-if="homeGoaliesCount > 0" class="delete-warning__breakdown-item">
              <span>{{ t('eventDeleteDialog.homeGoalies') }}: {{ homeGoaliesCount }}</span>
            </div>
            <div v-if="awayPlayersCount > 0" class="delete-warning__breakdown-item">
              <span>{{ t('eventDeleteDialog.awayPlayers') }}: {{ awayPlayersCount }}</span>
            </div>
            <div v-if="awayGoaliesCount > 0" class="delete-warning__breakdown-item">
              <span>{{ t('eventDeleteDialog.awayGoalies') }}: {{ awayGoaliesCount }}</span>
            </div>
          </template>
          <template v-else>
            <div v-if="playersCount > 0" class="delete-warning__breakdown-item">
              <span>{{ t('eventDeleteDialog.players') }}: {{ playersCount }}</span>
            </div>
            <div v-if="goaliesCount > 0" class="delete-warning__breakdown-item">
              <span>{{ t('eventDeleteDialog.goalies') }}: {{ goaliesCount }}</span>
            </div>
          </template>
        </div>

        <!-- Warning message -->
        <div class="delete-warning__message">
          <v-icon size="18" color="error" class="mr-1">mdi-information-outline</v-icon>
          <span>
            {{ t('eventDeleteDialog.warningPrefix') }} <strong>{{ t('eventDeleteDialog.warningStrong') }}</strong>. {{ t('eventDeleteDialog.warningSuffix') }}
          </span>
        </div>
      </div>

      <!-- No attendants -->
      <div v-else class="delete-warning__info">
        <p class="delete-warning__no-attendants">{{ t('eventDeleteDialog.noAttendants') }}</p>
        <p class="delete-warning__confirm-text">{{ t('eventDeleteDialog.confirmText') }}</p>
      </div>

      <!-- Actions -->
      <div class="delete-warning__actions">
        <button class="delete-warning__btn delete-warning__btn--cancel" @click="cancel">
          {{ t('eventDeleteDialog.cancelButton') }}
        </button>
        <button class="delete-warning__btn delete-warning__btn--delete" @click="confirm">
          <v-icon size="18" class="mr-1">mdi-trash-can-outline</v-icon>
          {{ t('eventDeleteDialog.confirmButton') }}
        </button>
      </div>
    </div>
  </CenteredModal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import CenteredModal from '@/components/CenteredModal.vue'
import type { IEvent } from '@/types'

const { t } = useI18n()

const props = defineProps<{
  show: boolean
  event: IEvent | null
}>()

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void
  (e: 'confirm-delete'): void
}>()

const localShow = computed({
  get: () => props.show,
  set: (val: boolean) => emit('update:show', val)
})

// --- Helpers ---
const arrLen = (arr: any) => (Array.isArray(arr) ? arr.length : 0)

const eventType = computed(() => props.event?.type ?? '')

// Does this event use homeAttendants/awayAttendants? (league, superliga, friendly-match)
const hasHomeAway = computed(() =>
  ['superliga', 'friendly-match'].includes(eventType.value) ||
  (!['training', 'camp', 'beer', 'blue-white'].includes(eventType.value) && (
    arrLen(props.event?.homeAttendants?.players) > 0 ||
    arrLen(props.event?.homeAttendants?.goalies) > 0 ||
    arrLen(props.event?.awayAttendants?.players) > 0 ||
    arrLen(props.event?.awayAttendants?.goalies) > 0
  ))
)

// Home/Away counts
const homePlayersCount = computed(() => arrLen(props.event?.homeAttendants?.players))
const homeGoaliesCount = computed(() => arrLen(props.event?.homeAttendants?.goalies))
const awayPlayersCount = computed(() => arrLen(props.event?.awayAttendants?.players))
const awayGoaliesCount = computed(() => arrLen(props.event?.awayAttendants?.goalies))

// Simple attendants counts (training, camp, beer, blue-white)
const playersCount = computed(() => arrLen(props.event?.attendants?.players))
const goaliesCount = computed(() => arrLen(props.event?.attendants?.goalies))

// Total
const totalAttendants = computed(() => {
  if (hasHomeAway.value) {
    return homePlayersCount.value + homeGoaliesCount.value + awayPlayersCount.value + awayGoaliesCount.value
  }
  return playersCount.value + goaliesCount.value
})

// --- Actions ---
const cancel = () => {
  emit('update:show', false)
}

const confirm = () => {
  emit('confirm-delete')
  emit('update:show', false)
}
</script>

<style lang="scss" scoped>
.delete-warning {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  &__icon {
    margin-bottom: 4px;
  }

  &__title {
    font-size: 20px;
    font-weight: 700;
    color: rgb(var(--v-theme-sheetText));
  }

  &__event-name {
    font-size: 15px;
    font-weight: 500;
    color: rgba(var(--v-theme-sheetText), 0.7);
    margin: 0;
  }

  &__info {
    width: 100%;
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__count {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    color: rgb(var(--v-theme-sheetText));
  }

  &__breakdown {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 16px;
    justify-content: center;
  }

  &__breakdown-item {
    font-size: 13px;
    color: rgba(var(--v-theme-sheetText), 0.6);
    padding: 4px 10px;
    border-radius: 8px;
    background: rgba(var(--v-theme-sheetText), 0.06);
  }

  &__message {
    display: flex;
    align-items: flex-start;
    gap: 4px;
    padding: 12px;
    border-radius: 12px;
    background: rgba(244, 67, 54, 0.08);
    border: 1px solid rgba(244, 67, 54, 0.2);
    font-size: 13px;
    line-height: 1.5;
    color: rgba(var(--v-theme-sheetText), 0.85);
    text-align: left;
  }

  &__no-attendants {
    font-size: 14px;
    color: rgba(var(--v-theme-sheetText), 0.5);
    text-align: center;
    margin: 0;
  }

  &__confirm-text {
    font-size: 14px;
    color: rgba(var(--v-theme-sheetText), 0.7);
    text-align: center;
    margin: 0;
  }

  &__actions {
    display: flex;
    gap: 12px;
    width: 100%;
    margin-top: 8px;
  }

  &__btn {
    flex: 1;
    padding: 12px 16px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &--cancel {
      background: rgba(var(--v-theme-sheetText), 0.08);
      color: rgb(var(--v-theme-sheetText));

      &:hover {
        background: rgba(var(--v-theme-sheetText), 0.14);
      }
    }

    &--delete {
      background: #f44336;
      color: #fff;

      &:hover {
        background: #d32f2f;
      }

      &:active {
        transform: scale(0.97);
      }
    }
  }
}
</style>
